import React from 'react';
import { fmtDateLabel, fmtDurationHM } from './vendor';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Thin day-bars strip — small DISCRETE bars, one per day. NOT a line chart.
 *
 * Colour is anomaly-only: a bar is amber where, and ONLY where, the server marks
 * that day `unusual` for this person. The threshold is the server's — never
 * recomputed here, and there is no target / 7-hour line. Everything else is the
 * ordinary blue. Heights are scaled to the busiest day purely for legibility;
 * they carry no threshold meaning.
 *
 * Day shape (verified live, fn_my_timesheet): { day, minutes, entries, unusual }.
 *
 * @param {{day:string, minutes:number, unusual:boolean}[]} days
 */
export default function DayBars({ days }) {
  if (!days || days.length === 0) return null;

  const maxMinutes = Math.max(1, ...days.map((d) => Number(d?.minutes) || 0));

  return (
    <div
      className="flex items-end gap-1"
      role="img"
      aria-label="Daily effort over the selected range"
      data-testid="day-bars"
    >
      {days.map((d) => {
        const minutes = Number(d?.minutes) || 0;
        const pct = Math.round((minutes / maxMinutes) * 100);
        const unusual = d?.unusual === true;
        // Every bar carries a permanent "MMM D" label so the range is readable
        // without hovering; the hover tooltip still gives the full date and
        // duration.
        const ym = String(d?.day || '');
        const monthIdx = ym ? Number(ym.slice(5, 7)) - 1 : -1;
        const dayNum = ym ? Number(ym.slice(8, 10)) : '';
        const label = monthIdx >= 0 ? `${MONTHS[monthIdx]} ${dayNum}` : `${dayNum}`;
        return (
          <div
            key={d?.day}
            className="flex-1 min-w-[3px] flex flex-col items-center gap-2.5"
            data-testid="day-bar"
            data-unusual={unusual ? 'true' : 'false'}
            title={`${fmtDateLabel(d?.day)} · ${fmtDurationHM(minutes * 60)}`}
          >
            <div className="w-full h-16 flex items-end">
              <div
                className={`w-full rounded-sm ${unusual ? 'bg-amber-500' : 'bg-[#32B1FF]'}`}
                // Floor the height so a zero/near-zero day is still a visible nub
                // rather than nothing — the bar's presence is the point.
                style={{ height: `${Math.max(4, pct)}%` }}
              />
            </div>
            {/* Labels stay horizontal where there's room (≥lg); on smaller
                screens they tilt -25° so "MMM D" fits under narrow bars without
                overlapping. Fixed height reserves room for the tilted text. */}
            <div className="flex h-8 w-full items-start justify-center overflow-visible lg:h-4">
              <span className="origin-top -rotate-[25deg] whitespace-nowrap text-[10px] leading-none text-gray-400 dark:text-gray-500 lg:rotate-0">
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
